// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@awc/App/Components/Common/XngZone";
import ProductLeadTime, { ProductLeadTimeStyles } from "@awc/App/Components/Product/ProductLeadTime";
import ProductSavePrice from "@awc/App/Components/Product/ProductSavePrice";
import { xIsAuthenticated } from "@awc/App/Store/Selectors/GeneralSelectors";
import { isExpeditedForm, isProductQuoteRequired } from "@awc/App/Store/Selectors/ProductSelectors";
import ProductAddToCartButton from "@awc/Overrides/Components/ProductAddToCartButton";
import { ProductAvailabilityStyles } from "@awc/Overrides/Components/ProductAvailability";
import ProductPrice, { ProductPriceStyles } from "@awc/Overrides/Components/ProductPrice";
import ProductQuantityOrdered from "@awc/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect, {
    ProductUnitOfMeasureSelectStyles,
} from "@awc/Overrides/Components/ProductUnitOfMeasureSelect";
import StyledBox, { StyledBoxStyles } from "@awc/XDL/Components/Common/Box";
import { mergeWithCss } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductShareLink, { ProductShareLinkStyles } from "@insite/content-library/Components/ProductShareLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useState } from "react";
import { connect } from "react-redux";

export interface ProductDetailsActionsBoxProps extends WidgetProps {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showVariants?: boolean;
    extendedStyles?: ProductDetailsActionsBoxStyles;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => ({
    isAuthenticated: xIsAuthenticated(state),
    configurationSelection: state.pages.productDetails.configurationSelection,
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    subscription: state.pages.productDetails.subscription,
    // XNG-Change
    canAddToCart: canAddToCart(
        state,
        props.productContext.product,
        props.productContext.productInfo,
        state.pages.productDetails.configurationCompleted,
        state.pages.productDetails.variantSelectionCompleted,
        state.pages.productDetails.configurationSelection,
    ),
});

type Props = ProductDetailsActionsBoxProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsActionsBoxStyles {
    container?: InjectableCss;
    box?: StyledBoxStyles;
    productDetailsVariantContainer?: InjectableCss;
    buyNowtext?: TypographyPresentationProps;
    qtyWrapper?: InjectableCss;
    multipleSaleQty?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListStyles?: ProductAddToListLinkStyles;
    buttonsWrapper?: InjectableCss;
    uomSelectWrapper?: InjectableCss;
    rfqBtn?: ButtonPresentationProps;
    buyBtn?: ButtonPresentationProps;
    unitOfMeasureSelect?: ProductUnitOfMeasureSelectStyles;
    shareLink?: ProductShareLinkStyles;
    shareWrapper?: InjectableCss;
    productLeadTime?: ProductLeadTimeStyles;
    formsWrapper?: InjectableCss;
}

export const productDetailsActionsBox: ProductDetailsActionsBoxStyles = {};

const ProductDetailsActions: FC<Props> = ({
    showVariants = true,
    extendedStyles,
    productContext,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    id,
    // XNG-Change
    canAddToCart,
}) => {
    const [styles] = useState(() => mergeWithCss(productDetailsActionsBox, extendedStyles));
    const [showRfqForm, setShowRfqForm] = useState(false);

    const isEditing = useShellContext().isEditing;

    const handleOpenRfqForm = () => {
        setShowRfqForm(!showRfqForm);
    };

    const { product, productInfo } = productContext;

    const isExpedited = isExpeditedForm(product, productInfo);
    const priceLoaded = product && productInfo && (productInfo.pricing || productInfo.failedToLoadPricing);

    return (
        <StyledWrapper>
            <ProductSavePrice />
            <StyledBox extendedStyles={styles.box}>
                <StyledWrapper {...styles.container}>
                    <Typography {...styles.buyNowtext}>{siteMessage("PDP_BuyItNow_Header")}</Typography>
                    <StyledWrapper {...styles.uomSelectWrapper}>
                        <ProductUnitOfMeasureSelect
                            displayPack={true}
                            extendedStyles={styles.unitOfMeasureSelect}
                            isPdp={true}
                        />
                    </StyledWrapper>

                    {showVariants && (
                        <StyledWrapper {...styles.productDetailsVariantContainer}>
                            {createWidgetElement("ProductDetails/ProductDetailsVariantOptions", {
                                fields: {},
                            })}
                        </StyledWrapper>
                    )}
                    {canAddToCart && (
                        <StyledWrapper {...styles.qtyWrapper}>
                            <VisuallyHidden as="label">{translate("Quantity")}</VisuallyHidden>
                            <ProductQuantityOrdered
                                showLabel={true}
                                labelOverride={translate("Quantity")}
                                data-test-selector="productQty"
                                configurationSelection={configurationSelection}
                                configurationCompleted={configurationCompleted}
                                variantSelectionCompleted={variantSelectionCompleted}
                            />
                        </StyledWrapper>
                    )}

                    {!priceLoaded ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            <ProductPrice
                                product={productContext}
                                showInvalidPriceMessage={true}
                                showLabel={true}
                                showPack={true}
                                extendedStyles={styles.productPriceStyles}
                            />
                            <ProductLeadTime extendedStyles={styles.productLeadTime} />
                            <ProductContextAvailability
                                extendedStyles={styles.productAvailabilityStyles}
                                isProductDetailsPage={true}
                            />
                            <StyledWrapper {...styles.buttonsWrapper}>
                                {canAddToCart && (
                                    <ProductAddToCartButton
                                        configurationSelection={configurationSelection}
                                        configurationCompleted={configurationCompleted}
                                        variantSelectionCompleted={variantSelectionCompleted}
                                        subscription={subscription}
                                        extendedStyles={styles.productAddToCartButtonStyles}
                                        data-test-selector="addToCartBtn"
                                    />
                                )}

                                <Button disabled={showRfqForm} onClick={handleOpenRfqForm} {...styles.rfqBtn}>
                                    {translate(`${isExpedited ? "Expedite" : "Submit Quick RFQ"}`)}
                                </Button>

                                {!isProductQuoteRequired(product, productInfo) && canAddToCart && (
                                    <ProductAddToCartButton
                                        extendedStyles={styles.buyBtn}
                                        configurationSelection={configurationSelection}
                                        configurationCompleted={configurationCompleted}
                                        variantSelectionCompleted={variantSelectionCompleted}
                                        subscription={subscription}
                                        data-test-selector="buyNowBtn"
                                        isBuyNow={true}
                                    />
                                )}
                            </StyledWrapper>
                        </>
                    )}
                    <StyledWrapper {...styles.shareWrapper}>
                        <ProductShareLink extendedStyles={styles.shareLink} />
                        <ProductAddToListLink
                            extendedStyles={styles.productAddToListStyles}
                            data-test-selector="productDetails_addToList"
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledBox>
            {(showRfqForm || isEditing) && (
                <StyledWrapper {...styles.formsWrapper}>
                    <XngZone hint="RfqFrom" zoneName="Content01" contentId={id} />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps)(ProductDetailsActions)),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
